import {
  IMobileNumberCheckResponse,
  IMobileNumberFixApplyResponse,
  IMobileNumberFixResponse,
} from "shared";

import { emptySplitApi } from "../baseApi";
import { orgHeader } from "../../../../common/lib/axiosBaseQuery";
import { CacheTags } from "../../types/cacheTags";

export const mobileNumberCheckApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchMobileNumberCheck: builder.query<IMobileNumberCheckResponse, string>({
      query: organisationId => ({
        url: `contact/mobileNumberCheck`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [{ type: CacheTags.MobileNumberCheck, id: `LIST` }],
    }),
    startMobileNumberCheck: builder.mutation<IMobileNumberCheckResponse, string>({
      query: organisationId => ({
        url: `contact/mobileNumberCheck`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      // Same shape as the status query: write it to that cache so progress shows & fast polling starts immediately
      async onQueryStarted(organisationId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(mobileNumberCheckApi.util.upsertQueryData(`fetchMobileNumberCheck`, organisationId, data));
        }
        catch {
          // Error toast comes from the rtk middleware
        }
      },
    }),
    fetchMobileNumberFix: builder.query<IMobileNumberFixResponse, string>({
      query: organisationId => ({
        url: `contact/mobileNumberFix`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    /** `contactIds` limits the run to those contacts, otherwise every invalid number in the org */
    startMobileNumberFix: builder.mutation<IMobileNumberFixResponse, { organisationId: string; contactIds?: number[] }>({
      query: ({ organisationId, contactIds }) => ({
        url: `contact/mobileNumberFix`,
        method: `POST`,
        data: { contactIds },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      // Same shape as the status query: write it to that cache so progress shows & polling starts immediately
      async onQueryStarted({ organisationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(mobileNumberCheckApi.util.upsertQueryData(`fetchMobileNumberFix`, organisationId, data));
        }
        catch {
          // Error toast comes from the rtk middleware
        }
      },
    }),
    /** Single contact: runs inline and returns the proposals, nothing to poll */
    fixContactMobileNumbers: builder.mutation<IMobileNumberFixResponse, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/mobileNumberFix`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    applyMobileNumberFix: builder.mutation<IMobileNumberFixApplyResponse, { organisationId: string; contactPersonIds: string[]; contactId?: number }>({
      query: ({ organisationId, contactPersonIds, contactId }) => ({
        url: `contact/mobileNumberFix/apply`,
        method: `POST`,
        data: { contactPersonIds, contactId },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [CacheTags.Contacts, CacheTags.SingleContact],
      // The response carries the remaining proposals
      async onQueryStarted({ organisationId, contactId }, { dispatch, queryFulfilled }) {
        try {
          const { data: { check, result } } = await queryFulfilled;

          // A single contact's result is held by its modal, not the org wide cache entry
          if (contactId) {
            return;
          }

          dispatch(mobileNumberCheckApi.util.upsertQueryData(`fetchMobileNumberFix`, organisationId, { check, result }));
        }
        catch {
          // Error toast comes from the rtk middleware
        }
      },
    }),
  }),
});

export const {
  useFetchMobileNumberFixQuery,
  useStartMobileNumberFixMutation,
  useFixContactMobileNumbersMutation,
  useApplyMobileNumberFixMutation,
  useFetchMobileNumberCheckQuery,
  useStartMobileNumberCheckMutation,
} = mobileNumberCheckApi;
